import { ORDER_PAGE as dict } from '@/resources/dict'
import { ServiceFee } from '@/resources/types'

export enum ServiceFeeIds {
  HeavyBagFee = 'heavy-bag-fee',
  BaseServiceFee = 'base-service-fee',
}

export type ServiceFeeDisplayRow = {
  id: ServiceFeeIds
  label: string
  value: number
  description?: string
}

export type OrderChargeContext = {
  total: number
  subtotal: number
  discount?: number
}

type IsBaseServiceFeeIncludedInShippingFeeParams = {
  shippingFee: number
  serviceFee: ServiceFee | null | undefined
  chargeContext: OrderChargeContext
}

type GetDisplayFreightParams = {
  shippingFee: number
  serviceFee?: ServiceFee | null
  chargeContext?: OrderChargeContext
}

const CHARGE_TOLERANCE = 0.011

const getChargeAddOnTotal = ({
  total,
  subtotal,
  discount = 0,
}: OrderChargeContext): number => total - subtotal + discount

const isBaseServiceFeeIncludedInShippingFee = ({
  shippingFee,
  serviceFee,
  chargeContext,
}: IsBaseServiceFeeIncludedInShippingFeeParams): boolean => {
  if (!shouldShowBaseServiceFee(serviceFee)) {
    return false
  }

  const chargeAddOnTotal = getChargeAddOnTotal(chargeContext)

  return Math.abs(chargeAddOnTotal - shippingFee) < CHARGE_TOLERANCE
}

export const shouldShowBaseServiceFee = (
  serviceFee?: ServiceFee | null
): boolean => (serviceFee?.baseServiceFee?.value ?? 0) > 0

export const shouldShowHeavyBagFee = (
  serviceFee?: ServiceFee | null
): boolean => {
  const heavyBagFee = serviceFee?.heavyBagFee

  return Boolean(heavyBagFee?.isHeavyBag || Number(heavyBagFee?.value) > 0)
}

export const isHeavyBagOrder = (serviceFee?: ServiceFee | null): boolean =>
  Boolean(serviceFee?.heavyBagFee?.isHeavyBag)

export const getDisplayFreight = ({
  shippingFee,
  serviceFee,
  chargeContext,
}: GetDisplayFreightParams): number => {
  if (serviceFee?.shippingFeeDisplayedAtCheckout != null) {
    return serviceFee.shippingFeeDisplayedAtCheckout
  }

  let displayFreight = shippingFee
  const heavyBagValue = serviceFee?.heavyBagFee?.value ?? 0
  const baseServiceFeeValue = serviceFee?.baseServiceFee?.value ?? 0

  if (shouldShowHeavyBagFee(serviceFee) && heavyBagValue > 0) {
    displayFreight -= heavyBagValue
  }

  if (chargeContext) {
    const hasBaseServiceFeeIncludedInShippingFee =
      isBaseServiceFeeIncludedInShippingFee({
        shippingFee,
        serviceFee,
        chargeContext,
      })

    if (hasBaseServiceFeeIncludedInShippingFee) {
      displayFreight -= baseServiceFeeValue
    }
  }

  return displayFreight
}

export const getServiceFeeDisplayRows = (
  serviceFee?: ServiceFee | null
): ServiceFeeDisplayRow[] => {
  const rows: ServiceFeeDisplayRow[] = []

  if (shouldShowHeavyBagFee(serviceFee)) {
    rows.push({
      id: ServiceFeeIds.HeavyBagFee,
      label: dict.printPage.heavyBag.heavyBag,
      value: serviceFee?.heavyBagFee?.value ?? 0,
      description: dict.heavyBagFeeTooltip,
    })
  }

  if (shouldShowBaseServiceFee(serviceFee)) {
    rows.push({
      id: ServiceFeeIds.BaseServiceFee,
      label: dict.printPage.heavyBag.convenience,
      value: serviceFee?.baseServiceFee?.value ?? 0,
      description: dict.convenienceFeeTooltip,
    })
  }

  return rows
}
